import React, { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import {
  Music2, Download, Loader2, Play, Sparkles, AudioWaveform, Disc3, Coins,
} from "lucide-react";

const MUSIC_CREDITS_COST = 2;

const EXAMPLE_PROMPTS = [
  "Luxury cinematic orchestral with deep brass, slow build, premium watch ad mood",
  "Tropical lounge house, summer cocktail, warm percussion and groovy bass",
  "Tense electronic trailer with deep risers and pounding drums, action ad",
  "Minimal acoustic guitar fingerpicking, cosy coffee shop in the morning",
  "Hip hop trap beat, confident swagger, gold luxury vibes",
  "Atmospheric ambient pad with soft piano, real-estate intro for high-end villa",
];

export default function MusicStudio() {
  const { user, checkAuth } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [instrumental, setInstrumental] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [progressSec, setProgressSec] = useState(0);
  const [library, setLibrary] = useState([]);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const credits = user?.credits ?? 0;
  const canAfford = credits >= MUSIC_CREDITS_COST;

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  useEffect(() => { fetchLibrary(); }, []);

  const fetchLibrary = async () => {
    try {
      const { data } = await api.get("/music/library", { params: { limit: 20 } });
      setLibrary(data.items || []);
    } catch (e) { console.warn("library load failed:", e?.message); }
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const startPolling = (projectId) => {
    timerRef.current = setInterval(() => setProgressSec(s => s + 1), 1000);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}`);
        if (data.status === "completed" && data.audio_url) {
          stopPolling();
          setResult(data); setLoading(false);
          toast.success("Musique générée !");
          fetchLibrary();
          checkAuth();   // refresh credits
        } else if (data.status === "failed") {
          stopPolling(); setLoading(false);
          const refundedMsg = data.refunded ? " (crédits remboursés)" : "";
          toast.error((data.error || "Génération échouée") + refundedMsg);
          checkAuth();   // refresh credits (refund may have applied)
        }
      } catch (e) { console.warn("poll:", e?.message); }
    }, 10000);
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Décrivez le style musical"); return; }
    if (!canAfford) {
      toast.error(`Crédits insuffisants (${credits}/${MUSIC_CREDITS_COST} requis)`);
      return;
    }
    stopPolling();
    setLoading(true); setResult(null); setProgressSec(0);
    try {
      const { data } = await api.post("/music/generate", { prompt, instrumental });
      if (!data?.project_id) throw new Error("Pas de project_id renvoyé");
      toast.info(`Suno compose votre musique — ${data.credits_charged} crédits débités, 1 à 3 min...`);
      checkAuth();   // immediate balance refresh
      startPolling(data.project_id);
    } catch (e) {
      setLoading(false);
      const status = e?.response?.status;
      if (status === 402) {
        toast.error(e.response.data?.detail || "Crédits insuffisants");
      } else {
        toast.error(e.response?.data?.detail || "Erreur démarrage Suno");
      }
    }
  };

  const download = (url, filename = "xookpub-music.mp3") => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    a.target = "_blank"; a.rel = "noopener noreferrer";
    a.click();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-mono">Studio · Musique IA</span>
          <h1 className="mt-2 text-4xl font-heading tracking-tighter">
            <span className="gold-text">Suno</span> compose la BO de votre pub
          </h1>
          <p className="mt-2 text-white/55 max-w-xl text-sm">
            Décrivez l&apos;ambiance, le style, l&apos;instrumentation : Suno crée une musique originale en 1 à 3 minutes.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-white/50 px-3 py-2 rounded-full border border-white/10">
            <Disc3 className="w-3 h-3 inline mr-1.5" /> Modèle V4.5 ALL
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Left controls */}
        <div className="lg:col-span-5 space-y-5">
          <div className="studio-card p-5">
            <label className="label-mono">Description / Brief musical</label>
            <textarea
              data-testid="music-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ex: cinematic orchestral, brass dramatique, build progressif, ambiance pub voiture de luxe..."
              className="mt-2 w-full h-32 bg-obsidian-light border border-white/10 rounded-lg p-3 outline-none focus:border-gold/50 text-sm resize-none"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {EXAMPLE_PROMPTS.map((ex) => (
                <button
                  key={ex}
                  data-testid={`music-ex-${ex.slice(0, 12).replace(/\W/g, '-')}`}
                  onClick={() => setPrompt(ex)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-gold/10 hover:text-gold border border-white/10 text-white/60 transition"
                >
                  {ex.slice(0, 42)}...
                </button>
              ))}
            </div>
          </div>

          <div className="studio-card p-5">
            <label className="label-mono">Type</label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                data-testid="music-instrumental"
                onClick={() => setInstrumental(true)}
                className={`p-4 rounded-lg border text-left ${instrumental ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}
              >
                <AudioWaveform className="w-5 h-5 text-gold mb-2" />
                <div className="font-heading text-sm">Instrumental</div>
                <div className="text-[10px] text-white/50 mt-0.5">Recommandé pour pubs</div>
              </button>
              <button
                data-testid="music-vocal"
                onClick={() => setInstrumental(false)}
                className={`p-4 rounded-lg border text-left ${!instrumental ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}
              >
                <Music2 className="w-5 h-5 text-gold mb-2" />
                <div className="font-heading text-sm">Avec voix</div>
                <div className="text-[10px] text-white/50 mt-0.5">Suno génère paroles + chant</div>
              </button>
            </div>
          </div>

          <div className="studio-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-gold" />
                <span className="label-mono">Coût par génération</span>
              </div>
              <span className="font-heading text-xl" data-testid="music-credit-cost">
                {MUSIC_CREDITS_COST} <span className="text-xs text-white/50">crédits</span>
              </span>
            </div>
            <div className="text-xs text-white/50 mb-3 flex items-center justify-between">
              <span>Solde actuel</span>
              <span data-testid="music-user-credits" className={canAfford ? "text-gold font-semibold" : "text-red-400 font-semibold"}>
                {credits} crédits
              </span>
            </div>
            <button
              data-testid="music-generate-btn"
              onClick={generate}
              disabled={loading || !canAfford}
              className="gold-btn w-full !py-4 text-base disabled:opacity-50"
            >
              {loading
                ? <><Loader2 className="w-5 h-5 animate-spin" /> Suno compose...</>
                : !canAfford
                  ? <><Coins className="w-5 h-5" /> Crédits insuffisants</>
                  : <><Sparkles className="w-5 h-5" /> Générer ma musique</>}
            </button>
            {!canAfford && (
              <Link to="/pricing" data-testid="music-buy-credits" className="block mt-3 text-center text-xs text-gold hover:underline">
                → Recharger mes crédits
              </Link>
            )}
          </div>
        </div>

        {/* Right preview + library */}
        <div className="lg:col-span-7 space-y-5">
          <div className="studio-card p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="label-mono">Aperçu</span>
              {result?.audio_url && (
                <button
                  data-testid="music-download"
                  onClick={() => download(result.audio_url, `xookpub-${result.song_title || "music"}.mp3`)}
                  className="ghost-btn !py-2 !px-4 text-sm"
                >
                  <Download className="w-4 h-4" /> Télécharger MP3
                </button>
              )}
            </div>
            <div className="aspect-[16/9] bg-obsidian rounded-xl border border-white/5 flex items-center justify-center overflow-hidden relative">
              {loading ? (
                <div className="text-center">
                  <Disc3 className="w-14 h-14 text-gold mx-auto animate-spin" style={{ animationDuration: "3s" }} />
                  <div className="mt-4 label-mono" data-testid="music-loading">Suno compose votre piste...</div>
                  <div className="mt-1 text-xs text-white/40">
                    {Math.floor(progressSec / 60)}min {progressSec % 60}s écoulés · 1 à 3 min en général
                  </div>
                </div>
              ) : result?.audio_url ? (
                <div className="w-full h-full flex flex-col">
                  {result.cover_url && (
                    <img
                      src={result.cover_url}
                      alt={result.song_title}
                      className="absolute inset-0 w-full h-full object-cover opacity-30"
                    />
                  )}
                  <div className="relative z-10 flex-1 flex flex-col justify-center items-center p-6 text-center">
                    <div className="font-heading text-2xl tracking-tight" data-testid="music-result-title">{result.song_title || "Nouvelle piste"}</div>
                    {result.tags && <div className="text-xs text-white/50 mt-2 max-w-md">{result.tags}</div>}
                    {result.duration_sec && (
                      <div className="text-xs text-gold mt-2">
                        {Math.round(result.duration_sec)}s
                      </div>
                    )}
                  </div>
                  <div className="relative z-10 p-4 bg-obsidian/80 backdrop-blur">
                    <audio
                      data-testid="music-result-player"
                      src={result.audio_url}
                      controls
                      className="w-full"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center text-white/30">
                  <Play className="w-12 h-12 mx-auto" />
                  <div className="mt-3 text-sm">Votre musique apparaîtra ici</div>
                </div>
              )}
            </div>
          </div>

          {/* Library */}
          {library.length > 0 && (
            <div className="studio-card p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="label-mono">Vos morceaux récents</span>
                <span className="text-xs text-white/40">{library.length}</span>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto scroll-thin">
                {library.map((m) => (
                  <div
                    key={m.project_id}
                    data-testid={`library-item-${m.project_id}`}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition"
                  >
                    <div className="w-10 h-10 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
                      <Music2 className="w-4 h-4 text-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{m.song_title || m.title}</div>
                      <div className="text-[11px] text-white/40">
                        {m.status === "completed"
                          ? `${Math.round(m.duration_sec || 0)}s · ${m.tags?.slice(0, 40) || "—"}`
                          : m.status === "failed"
                            ? <span className="text-red-400">échec</span>
                            : <span className="text-amber-400">génération en cours...</span>}
                      </div>
                    </div>
                    {m.audio_url && (
                      <button
                        onClick={() => download(m.audio_url, `xookpub-${m.song_title || m.project_id}.mp3`)}
                        className="ghost-btn !py-1.5 !px-3 text-xs"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
